import { $http } from './http';
import { $log } from './debug';
import { YObjectId, YMaybeObjectId } from './types';

export namespace $api {
    export function errorHandler(response: any): never {
        if(response?.data?.data?.reason)
            throw response.data.data.reason;
        else if(response?.data?.data?.sv)
            throw response.data.data.sv;
        else if(response?.data?.message)
            throw response.data.message;

        $log('app', 'Unknown API error', response);
        throw 'Something went wrong. Try again later.'
    }


    interface IDModel { id: YObjectId };

    interface Store<Model, Object> {
        all: Object[];
        init(models: Model[]): Object[];
        register(model: Model): Object;
        getAll(reload?: boolean): Promise<Object[]>;
        get(id: YObjectId): Promise<Object>;
        maybe(id: YMaybeObjectId): Object | undefined;
        update(id: YObjectId, params: any): Promise<Object>;
        insert(params: any): Promise<Object>;
        upsert(id: YObjectId | undefined, params: any): Promise<Object>;
        remove(id: YObjectId): Promise<void>;
    }

    export function makeStore<Model extends IDModel, Object>(all_path: string, one_path: string, mapper: (m: Model) => Object, updater: (o: Object, m: Model) => void): Store<Model, Object> {
        let all_promise: Promise<Object[]> | undefined;
        const all: Object[] = [];
        let all_by_id: { [id: YObjectId]: Object } = {};

        function registerAll(models: Model[]) {
            all.length = 0;
            all_by_id = {};
            for(const m of models) {
                const id = m.id;
                const obj = mapper(m);
                all.push(obj);
                all_by_id[id] = obj;
            }

            if(!all_promise)
                all_promise = Promise.resolve(all);

            return all;
        }

        function register(m: Model) {
            const id = m.id;
            let obj = all_by_id[id];
            if(obj) {
                updater(obj, m);
            } else {
                obj = mapper(m);
                all.push(obj);
                all_by_id[id] = obj;
            }

            return obj;
        }

        return {
            all: all,
            init: registerAll,
            register: register,
            getAll: (reload?: boolean) => {
                if(!all_promise || reload) {
                    all_promise = $http.GET<Model[]>('/api/v1/' + all_path).then(models => {
                        return registerAll(models);
                    }, errorHandler);
                }

                return all_promise;
            },
            get: (id: YObjectId) => {
                const obj = all_by_id[id];
                if(obj)
                    return Promise.resolve(obj);

                return $http.GET<Model>('/api/v1/' + one_path + '/' + id).then(model => {
                    return register(model);
                }, errorHandler);
            },
            maybe: (id: YObjectId) => all_by_id[id],
            update: (id: YObjectId, params: any) => {
                return $http.PUT<Model>('/api/v1/' + one_path + '/' + id, params).then(model => {
                    return register(model);
                }, errorHandler);
            },
            insert: (params: any) => {
                return $http.POST<Model>('/api/v1/' + one_path, params).then(model => {
                    return register(model);
                }, errorHandler);
            },
            upsert: (id: YObjectId | undefined, params: any) => {
                if(id != null) {
                    return $http.PUT<Model>('/api/v1/' + one_path + '/' + id, params).then(model => {
                        return register(model);
                    }, errorHandler);
                } else {
                    return $http.POST<Model>('/api/v1/' + one_path, params).then(model => {
                        return register(model);
                    }, errorHandler);
                }
            },
            remove: (id: YObjectId) => {
                return $http.DELETE('/api/v1/' + one_path + '/' + id).then(() => {
                    const obj = all_by_id[id];
                    if(obj) {
                        const index = all.indexOf(obj);
                        if(index >= 0)
                            all.splice(index, 1);
                    }
                }, errorHandler);
            }
        }
    }

    export function GET<Data = void>(path: string) {
        return $http.GET<Data>(`/api/v1/${path}`).catch(errorHandler);
    }

    export function PUT<Data = void>(path: string, data?: any) {
        return $http.PUT<Data>(`/api/v1/${path}`, data).catch(errorHandler);
    }

    export function POST<Data = void>(path: string, data?: any) {
        return $http.POST<Data>(`/api/v1/${path}`, data).catch(errorHandler);
    }

    export function DELETE(path: string) {
        return $http.DELETE(`/api/v1/${path}`).catch(errorHandler);
    }
}